import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BusinessAreaSummary } from '../types';
import businessAreaService from '../services/businessAreaService';

/**
 * Was ein Vorhaben eingebracht und was es gekostet hat.
 *
 * Eine Tabelle und keine Grafik: es sind wenige Zeilen, und verglichen werden exakte Beträge —
 * dafür ist eine Balkenlänge die schlechtere Auskunft. Die Karte erscheint erst, wenn überhaupt
 * etwas zugeordnet ist; vorher bestünde sie aus einer einzigen Zeile „Nicht zugeordnet".
 *
 * Alles netto, weil die Umsatzsteuer auf beiden Seiten durchlaufender Posten ist. Und alles ohne
 * Arbeitszeit: was die eigene Stunde gekostet hat, steht nirgends in diesem System — das hier ist
 * ein Deckungsbeitrag über die Auszahlungen, kein Gewinn. Der Hinweis unter der Tabelle sagt das,
 * weil eine Zahl, die man für etwas anderes hält, schlimmer ist als keine.
 */

const euro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const BusinessAreaCard: React.FC = () => {
  const [data, setData] = useState<BusinessAreaSummary | null>(null);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    businessAreaService.summary(year)
      .then(s => { if (!cancelled) { setData(s); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  if (loading || !data) return null;

  const assigned = data.areas.filter(a => a.area !== null);
  if (assigned.length === 0) return null;

  const unassigned = data.areas.find(a => a.area === null);

  return (
    <div className="rounded-xl p-5 mb-6 animate-fade-up"
      style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>

      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Vorhaben
        </h2>
        {data.availableYears.length > 1 && (
          <select
            value={data.year}
            onChange={e => setYear(Number(e.target.value))}
            className="text-xs rounded px-2 py-1"
            style={{
              background: 'var(--input-bg)',
              border: '1px solid var(--surface-border)',
              color: 'var(--text-primary)',
            }}
            aria-label="Jahr"
          >
            {data.availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </div>

      <table className="w-full text-sm nums-tabular">
        <thead>
          <tr className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            <th className="text-left font-semibold pb-2">Bereich</th>
            <th className="text-right font-semibold pb-2">Erlös</th>
            <th className="text-right font-semibold pb-2">Kosten</th>
            <th className="text-right font-semibold pb-2">Ergebnis</th>
          </tr>
        </thead>
        <tbody>
          {assigned.map(a => (
            <tr key={a.area} style={{ borderTop: '1px solid var(--surface-border)' }}>
              <td className="py-2 pr-3" style={{ color: 'var(--text-primary)' }}>
                <Link
                  to={`/invoices?businessArea=${encodeURIComponent(a.area!)}`}
                  className="font-medium hover:underline"
                >
                  {a.area}
                </Link>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {a.invoiceCount} {a.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen'}
                  {a.transactionCount > 0 && ` · ${a.transactionCount} ${a.transactionCount === 1 ? 'Ausgabe' : 'Ausgaben'}`}
                </div>
              </td>
              <td className="text-right py-2 pl-3 align-top" style={{ color: 'var(--text-primary)' }}>
                {euro(a.revenueNetCents)}
                {/* Der Erlös zählt ab dem Rechnungsdatum. Was davon wirklich da ist, ist die
                    andere Frage — und bei einem Vorhaben, das gerade erst verkauft, die
                    wichtigere. */}
                {a.paidNetCents !== a.revenueNetCents && (
                  <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    {euro(a.paidNetCents)} bezahlt
                  </div>
                )}
              </td>
              <td className="text-right py-2 pl-3 align-top" style={{ color: 'var(--text-primary)' }}>
                {euro(a.costNetCents)}
              </td>
              <td className="text-right py-2 pl-3 align-top font-bold"
                style={{ color: a.resultNetCents < 0 ? 'var(--danger, #dc2626)' : 'var(--text-primary)' }}>
                {euro(a.resultNetCents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Solange hier ein großer Betrag liegt, ist jede Zahl darüber nur die halbe Wahrheit:
          ein Vorhaben, dessen Serverrechnung noch unzugeordnet ist, sieht ertragreicher aus,
          als es ist. Deshalb steht es da und wird nicht weggelassen. */}
      {unassigned && (unassigned.costNetCents > 0 || unassigned.revenueNetCents > 0) && (
        <p className="text-xs mt-3 pt-3" style={{ color: 'var(--text-muted)', borderTop: '1px solid var(--surface-border)' }}>
          Nicht zugeordnet: {euro(unassigned.revenueNetCents)} Erlös, {euro(unassigned.costNetCents)} Kosten
          {' · '}
          <Link to="/banking" className="underline">Umsätze zuordnen</Link>
        </p>
      )}

      <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
        Netto. Erlöse ab Rechnungsdatum, Kosten ab Kontoauszug — ohne eigene Arbeitszeit.
      </p>
    </div>
  );
};

export default BusinessAreaCard;
